import React, { useEffect, useCallback, useState } from "react";

import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import Button from "@material-ui/core/Button";
import FileUploadInput from "components/FileUploadInput";
import { isBlank, notBlank } from "../../util/ext";
import CircularProgress from "@material-ui/core/CircularProgress";
import {
  Box,
  DialogContentText,
  DialogTitle,
  List,
  ListItem,
} from "@material-ui/core";
import { useErrorBox } from "../../manage-organizations/edit/use-errorBox";
import PhotoOutlinedIcon from "@material-ui/icons/PhotoOutlined";
import {
  useProgramImages,
  useProgramImageDelete,
  useProgramImageUpload,
} from "../cspServices";
import Grid from "@material-ui/core/Grid";
import { equals, map, not } from "ramda";
import PanelLoadingIndicator from "../../components/PanelLoadingIndicator";
import ConfirmButton from "../../components/ConfirmButton";

const ProgramImagesUploadDialog = ({ project, onCancel, onApply, open }) => {
  const [previewUrl, setPreviewUrl] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);

  const [programImages, setProgramImages] = useState([]);
  const programImagesResponse = useProgramImages(project.program.programKey);
  useEffect(() => {
    if (programImagesResponse.isSuccess) {
      setProgramImages(programImagesResponse.data);
    }
  }, [programImagesResponse.isSuccess, programImagesResponse.data]);

  const onFileInputChange = file => {
    setImageFile(file);
    if (notBlank(file)) {
      const reader = new FileReader();
      reader.onload = () => {
        const dataUrl = reader.result;
        setPreviewUrl(dataUrl);
      };
      reader.readAsDataURL(file);
      removeValidationError("imageFile");
    } else {
      setPreviewUrl(null);
    }
  };

  const {
    setGeneralError,
    addValidationError,
    firstValidationError,
    removeValidationError,
    removeValidationErrors,
    ErrorBox: DialogErrors,
  } = useErrorBox();

  const imageUpload = useProgramImageUpload();
  const onUploadClick = async () => {
    if (notBlank(imageFile)) {
      setUploading(true);
      try {
        await imageUpload.mutateAsync({
          resourceIds: { programKey: project.program.programKey },
          data: imageFile,
        });
      } catch (err) {
        setGeneralError(err);
      } finally {
        setUploading(false);
      }
    } else {
      addValidationError({
        imageFile: ["An image must be selected for upload."],
      });
    }
  };

  const imageDelete = useProgramImageDelete();
  const handleDeleteClick = useCallback(
    async filename => {
      setDeleting(true);

      try {
        await imageDelete.mutateAsync({
          resourceIds: {
            programKey: project.program.programKey,
            filename,
          },
        });
      } catch (err) {
        setGeneralError(err);
      } finally {
        setDeleting(false);
      }
    },
    [project.program.programKey, imageDelete, setGeneralError],
  );

  const handleImageClick = programImage => {
    setSelectedImage(programImage);
  };

  const handleApply = () => {
    if (notBlank(selectedImage)) {
      removeValidationErrors();
      onApply(selectedImage);
    } else {
      addValidationError({
        imageSelect: ["An image from the list must be selected."],
      });
    }
  };

  return (
    <Dialog open={open} maxWidth="md">
      <DialogTitle id="program-images-dialog-title">
        {"Images uploaded for " + project.program.programName}
      </DialogTitle>
      <DialogContent>
        <DialogContentText>
          Choose an existing image from the list associated with the Program or
          upload a new one.
        </DialogContentText>
        <DialogErrors showValidationErrors />

        <Box padding={2}>
          {(programImagesResponse.isLoading || deleting) && (
            <PanelLoadingIndicator />
          )}
          {isBlank(programImages) && (
            <Box padding={5} fontSize="1.4rem" fontStyle="italic">
              No Images uploaded for this Program.
            </Box>
          )}

          {notBlank(programImages) && not(deleting) && (
            <Box border={1} borderColor="primary.darker" mb={6}>
              <List dense style={{ maxHeight: 500, overflowY: "auto" }}>
                {map(
                  pi => (
                    <ListItem
                      divider
                      key={pi.filename}
                      selected={equals(selectedImage, pi)}
                      onClick={() => handleImageClick(pi)}
                    >
                      <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                        width="100%"
                      >
                        <Box
                          display="flex"
                          justifyContent="center"
                          alignItems="center"
                          width={120}
                          height={120}
                          bgcolor="primary.dark"
                          margin={2}
                        >
                          <img
                            style={{ maxWidth: 100, maxHeight: 100 }}
                            alt={pi.filename}
                            src={pi.imageUrl}
                          />
                        </Box>
                        <Box margin={2}>{pi.filename}</Box>
                        <Box margin={2}>
                          <ConfirmButton
                            handleDelete={() => handleDeleteClick(pi.filename)}
                            dialogTitle="Remove Library Option"
                            dialogBody="Are you sure you want to remove this library option?"
                          />
                        </Box>
                      </Box>
                    </ListItem>
                  ),
                  programImages,
                )}
              </List>
            </Box>
          )}

          <DialogContentText>
            Choose an existing image from the list associated with the Program
            or upload a new one.
          </DialogContentText>
          <Box
            marginTop={6}
            marginBottom={6}
            display="flex"
            alignItems="flex-end"
          >
            <Grid container spacing={3} alignItems="flex-end">
              <Grid item md={5}>
                <FileUploadInput
                  value={imageFile}
                  accept=".jpg, .jpeg, .png, image/png, image/jpg, image/jpeg"
                  disabled={uploading}
                  error={firstValidationError("imageFile")}
                  onChange={onFileInputChange}
                />
              </Grid>
              <Grid item md={4}>
                <Box
                  minWidth={100}
                  minHeight={100}
                  bgcolor="primary.dark"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                >
                  {uploading ? (
                    <CircularProgress />
                  ) : isBlank(previewUrl) ? (
                    <PhotoOutlinedIcon style={{ fontSize: 60 }} />
                  ) : (
                    <img
                      style={{ maxWidth: 100, maxHeight: 100 }}
                      src={previewUrl}
                      alt="preview"
                    />
                  )}
                </Box>
              </Grid>
              <Grid item md={3}>
                <Button variant="outlined" onClick={onUploadClick}>
                  Upload
                </Button>
              </Grid>
            </Grid>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button disabled={uploading} onClick={onCancel} color="primary">
          Cancel
        </Button>
        <Button disabled={uploading} onClick={handleApply} color="primary">
          Apply
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ProgramImagesUploadDialog;
